import { Overlay, OverlayPositionBuilder, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  type ComponentRef,
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  numberAttribute,
  type OnDestroy,
  type OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  runInInjectionContext,
  signal,
  type TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import type { ClassValue } from 'clsx';
import { filter, map, of, Subject, switchMap, tap, timer } from 'rxjs';

import { TOOLTIP_FALLBACK_POSITION, TOOLTIP_POSITIONS_MAP, TOOLTIP_VIEWPORT_MARGIN } from '@solvace/zard/shared/components/tooltip/tooltip-positions';
import { tooltipVariants, type ZardTooltipPositionVariants } from '@solvace/zard/shared/components/tooltip/tooltip.variants';
import { ZardIdDirective } from '@solvace/zard/shared/core';
import { ZardStringTemplateOutletDirective } from '@solvace/zard/shared/core/directives/string-template-outlet/string-template-outlet.directive';
import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

export type ZardTooltipTriggers = 'click' | 'hover';
export type ZardTooltipType = string | TemplateRef<void> | null;

interface DelayConfig {
  isShow: boolean;
  delay: number;
}

const throttle = (callback: () => void, wait: number): (() => void) => {
  let time = Date.now();
  return function (): void {
    if (time + wait - Date.now() < 0) {
      callback();
      time = Date.now();
    }
  };
};

@Directive({
  standalone: true,
  selector: '[zTooltip]',
  exportAs: 'zTooltip',
})
export class ZardTooltipDirective implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);
  private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly renderer = inject(Renderer2);

  private delaySubject?: Subject<DelayConfig>;
  private componentRef?: ComponentRef<ZardTooltipComponent>;
  private listenersRefs: (() => void)[] = [];
  private overlayRef?: OverlayRef;
  private ariaEffectRef?: ReturnType<typeof effect>;

  readonly zPosition = input<ZardTooltipPositionVariants>('bottom');
  readonly zTrigger = input<ZardTooltipTriggers>('hover');
  readonly zTooltip = input<ZardTooltipType>(null);
  readonly zShowDelay = input(150, { transform: numberAttribute });
  readonly zHideDelay = input(100, { transform: numberAttribute });

  readonly zTooltipClass = input<ClassValue>('');
  /** Additive, defaults to current behavior (no max-width, never wraps). Set true to cap the
   *  tooltip at 260px and let long text wrap onto multiple lines — see RCA Notification Management. */
  readonly zConstrainWidth = input(false);
  /** Additive, defaults to current behavior (hides on click and focusout). Set true to keep the tooltip
   *  open while the pointer stays on the host, e.g. a dropdown trigger whose menu takes focus on open. */
  readonly zKeepOnPointerHover = input(false);

  private isPointerOver = false;

  readonly zShow = output<void>();
  readonly zHide = output<void>();

  private readonly tooltipText = computed<string | TemplateRef<void>>(() => {
    let tooltipText = this.zTooltip();
    if (!tooltipText) {
      return '';
    } else if (typeof tooltipText === 'string') {
      tooltipText = tooltipText.trim();
    }
    return tooltipText;
  });

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const positionStrategy = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elementRef)
        .withPositions([TOOLTIP_POSITIONS_MAP[this.zPosition()], TOOLTIP_POSITIONS_MAP[TOOLTIP_FALLBACK_POSITION[this.zPosition()]]])
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(TOOLTIP_VIEWPORT_MARGIN);
      this.overlayRef = this.overlay.create({ positionStrategy });

      runInInjectionContext(this.injector, () => {
        toObservable(this.zTrigger)
          .pipe(
            tap(() => {
              this.setupDelayMechanism();
              this.cleanupTriggerEvents();
              this.initTriggers();
            }),
            filter(() => !!this.overlayRef),
            switchMap(() => (this.overlayRef as OverlayRef).outsidePointerEvents()),
            filter((event) => !this.elementRef.nativeElement.contains(event.target)),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(() => this.delay(false, 0));
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up any pending effect
    if (this.ariaEffectRef) {
      this.ariaEffectRef.destroy();
      this.ariaEffectRef = undefined;
    }

    this.delaySubject?.complete();
    this.cleanupTriggerEvents();
    this.overlayRef?.dispose();
  }

  private initTriggers(): void {
    this.initScrollListener();
    this.initClickListeners();
    this.initHoverListeners();
  }

  private initClickListeners(): void {
    if (this.zTrigger() !== 'click') {
      return;
    }

    this.listenersRefs = [
      ...this.listenersRefs,
      this.renderer.listen(this.elementRef.nativeElement, 'click', () => {
        const shouldShowTooltip = !this.overlayRef?.hasAttached();
        const delay = shouldShowTooltip ? this.zShowDelay() : this.zHideDelay();
        this.delay(shouldShowTooltip, delay);
      }),
    ];
  }

  private initHoverListeners(): void {
    if (this.zTrigger() !== 'hover') {
      return;
    }

    this.listenersRefs = [
      ...this.listenersRefs,
      this.renderer.listen(this.elementRef.nativeElement, 'mouseenter', () => {
        this.isPointerOver = true;
        this.delay(true, this.zShowDelay());
      }),
      this.renderer.listen(this.elementRef.nativeElement, 'mouseleave', () => {
        this.isPointerOver = false;
        this.delay(false, this.zHideDelay());
      }),
      this.renderer.listen(this.elementRef.nativeElement, 'focusin', () => this.delay(true, this.zShowDelay())),
      this.renderer.listen(this.elementRef.nativeElement, 'focusout', () => {
        if (!this.shouldKeepOnPointerHover()) {
          this.delay(false, this.zHideDelay());
        }
      }),
      this.renderer.listen(this.elementRef.nativeElement, 'click', () => {
        if (this.shouldKeepOnPointerHover()) {
          this.delay(true, 0);
          return;
        }
        this.delay(false, 0);
      }),
    ];
  }

  private shouldKeepOnPointerHover(): boolean {
    return this.zKeepOnPointerHover() && this.isPointerOver;
  }

  private initScrollListener(): void {
    this.listenersRefs = [
      ...this.listenersRefs,
      this.renderer.listen(
        this.document.defaultView,
        'scroll',
        throttle(() => this.delay(false, 0), 100)
      ),
    ];
  }

  private cleanupTriggerEvents(): void {
    for (const eventRef of this.listenersRefs) {
      eventRef();
    }
    this.listenersRefs = [];
  }

  private delay(isShow: boolean, delay = -1): void {
    this.delaySubject?.next({ isShow, delay });
  }

  private setupDelayMechanism(): void {
    this.delaySubject?.complete();
    this.delaySubject = new Subject<DelayConfig>();

    this.delaySubject
      .pipe(
        switchMap((config) => (config.delay < 0 ? of(config) : timer(config.delay).pipe(map(() => config)))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((config) => {
        if (config.isShow) {
          this.show();
        } else {
          this.hide();
        }
      });
  }

  private show(): void {
    if (this.componentRef && this.zKeepOnPointerHover()) {
      this.bringToFront();
      return;
    }
    if (this.componentRef || !this.tooltipText()) {
      return;
    }

    const tooltipPortal = new ComponentPortal(ZardTooltipComponent);
    this.componentRef = this.overlayRef?.attach(tooltipPortal);
    if (this.zKeepOnPointerHover()) {
      this.bringToFront();
    }
    this.componentRef?.onDestroy(() => {
      this.componentRef = undefined;
    });
    this.componentRef?.instance.state.set('opened');
    this.componentRef?.instance.setProps(this.tooltipText(), this.zPosition(), this.zTooltipClass(), this.zConstrainWidth());
    this.componentRef?.changeDetectorRef.detectChanges();
    this.overlayRef?.updatePosition();
    runInInjectionContext(this.injector, () => {
      this.ariaEffectRef = effect(() => {
        const tooltipId = this.componentRef?.instance.uniqueId()?.id();
        if (tooltipId) {
          this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-describedby', tooltipId);
          this.ariaEffectRef?.destroy();
          this.ariaEffectRef = undefined;
        }
      });
    });
    this.zShow.emit();
  }

  private bringToFront(): void {
    const host = this.overlayRef?.hostElement;
    host?.parentElement?.appendChild(host);
  }

  private hide(): void {
    if (!this.componentRef) {
      return;
    }

    // Clean up any pending effect
    if (this.ariaEffectRef) {
      this.ariaEffectRef.destroy();
      this.ariaEffectRef = undefined;
    }

    this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    this.componentRef.instance.state.set('closed');
    this.zHide.emit();
    this.overlayRef?.detach();
  }
}

@Component({
  standalone: true,
  selector: 'z-tooltip',
  imports: [ZardIdDirective, ZardStringTemplateOutletDirective],
  template: `
    <span
      zardId="tooltip"
      #z="zardId"
    >
      <ng-container *zStringTemplateOutlet="tooltipText()">{{ tooltipText() }}</ng-container>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
    '[attr.id]': 'tooltipId()',
    '[attr.data-side]': 'position()',
    '[attr.data-state]': 'state()',
    role: 'tooltip',
  },
})
export class ZardTooltipComponent {

  protected readonly constrainWidth = signal(false);
  /** AB#72289 — additive: `customClass()` defaults to `''`, so `mergeClasses` returns exactly
   *  `tooltipVariants()` for every existing consumer. See `ZardTooltipDirective.zTooltipClass`. */
  protected readonly classes = computed(() => mergeClasses(tooltipVariants({ constrainWidth: this.constrainWidth() }), this.customClass()));
  protected readonly position = signal<ZardTooltipPositionVariants>('bottom');
  protected readonly customClass = signal<ClassValue>('');
  readonly state = signal<'closed' | 'opened'>('closed');
  readonly uniqueId = viewChild<ZardIdDirective>('z');
  protected readonly tooltipText = signal<ZardTooltipType>(null);
  protected readonly tooltipId = computed(() => this.uniqueId()?.id() ?? 'tooltip');

  setProps(tooltipText: ZardTooltipType, position: ZardTooltipPositionVariants, customClass: ClassValue = '', constrainWidth = false): void {
    this.constrainWidth.set(constrainWidth);
    if (tooltipText) {
      this.tooltipText.set(tooltipText);
    }
    this.position.set(position);
    this.customClass.set(customClass);
  }
}
